import { useState } from "react";
import useI18n from "@/hooks/useI18n";
import Alert from "@/atomic/atoms/Alert";
import DebtPassageCard from "@/atomic/molecules/DebtPassageCard";
import PassagesCountStatus from "@/atomic/molecules/PassagesCountStatus";
import ScrapersConsultationCard from "@/atomic/molecules/ScrapersConsultationCard";
import {
  createPendingConsultation,
  ScrapersConsultation,
} from "@/domain/scrapers/consultation";
import {
  FormattedDebts,
  areAllSelectableDebtsSelected,
} from "@/utils/transform";
import SelectionMarker from "@/atomic/atoms/SelectionMarker";

const VISIBLE_PASSAGES_COUNT = 2;

interface PartnerPassagesResultProps {
  items: FormattedDebts[];
  selectables: { name: string; checked: boolean }[] | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  plate: string;
  consultation?: ScrapersConsultation;
  isSearching?: boolean;
}

export default function PartnerPassagesResult({
  items,
  selectables,
  onChange,
  plate,
  consultation,
  isSearching = false,
}: PartnerPassagesResultProps) {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasHiddenPassages = items.length > VISIBLE_PASSAGES_COUNT;
  const visibleItems = isExpanded
    ? items
    : items.slice(0, VISIBLE_PASSAGES_COUNT);
  const areAllSelected = areAllSelectableDebtsSelected(items, selectables);
  const selectAllLabel = t("pages:home:selectAllPassages", {
    count: items.length,
  });

  const handleSelectAll = () => {
    if (isSearching) return;
    onChange({
      target: { name: "all", checked: !areAllSelected },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const scrapersConsultation = consultation ?? createPendingConsultation();

  return (
    <div className="flex flex-col">
      <p className="pt-6 text-base font-primary text-text leading-[1.4]">
        {t("pages:home:foundDebtsDescription")}
      </p>

      <div
        className={`flex items-center gap-4 pt-6 pb-4 ${
          isSearching ? "opacity-50" : ""
        }`}
      >
        <span
          role="checkbox"
          aria-checked={areAllSelected}
          aria-disabled={isSearching}
          aria-label={selectAllLabel}
          tabIndex={isSearching ? -1 : 0}
          onClick={handleSelectAll}
          onKeyDown={(e) => {
            if (isSearching) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleSelectAll();
            }
          }}
          className={
            isSearching ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"
          }
        >
          <SelectionMarker selected={areAllSelected} aria-hidden />
        </span>
        <PassagesCountStatus label={selectAllLabel} isSearching={isSearching} />
      </div>

      <div className="flex flex-col gap-4">
        {visibleItems.map((item, index) => {
          const isSelected =
            selectables?.some((selectable) => selectable.name === item.id) ??
            false;
          return (
            <DebtPassageCard
              key={item.id || index}
              item={item}
              isSelected={isSelected}
              onChange={isSearching ? undefined : onChange}
              formatPlate={(itemPlate) => itemPlate || plate}
              showSelectionMarker
              layout="site"
            />
          );
        })}
      </div>

      {hasHiddenPassages && !isExpanded && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="mt-6 text-sm font-bold font-primary text-primary underline leading-[1.4]"
        >
          {t("pages:home:seeAllPassages")}
        </button>
      )}

      <div className="w-full h-px bg-border mt-6" />

      <p className="mt-6 text-base font-medium font-secondary text-text text-center tracking-[-0.4px] leading-[20.8px]">
        {t("pages:home:passagesInOtherConcessionaires")}
      </p>

      <div className="mt-6">
        <Alert variant="highlight" showIcon={false}>
          {t("pages:home:passagesPayOnConcessionaireSite")}
        </Alert>
      </div>

      <div className="mt-6">
        <ScrapersConsultationCard consultation={scrapersConsultation} />
      </div>
    </div>
  );
}
